import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { FileSignature, Check, ArrowLeft, Printer, ShieldCheck } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

export default function ManagerAgreement() {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [fullName, setFullName] = useState("");
  const [signature, setSignature] = useState("");
  const [payoutEmail, setPayoutEmail] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);

  usePageMeta({
    title: "Game Manager Agreement · PickemSports.club",
    path: "/manager/agreement",
    noindex: true,
  });

  const load = () =>
    api.get("/friends/manager/agreement").then(({ data }) => {
      setDoc(data);
      setFullName((prev) => prev || data.record?.full_name || data.contractor_name || "");
    });
  useEffect(() => { load().catch(() => {}); }, []);

  const sign = async () => {
    setBusy(true);
    try {
      await api.post("/friends/manager/agreement", {
        full_name: fullName, signature, accept,
        payout_email: payoutEmail || undefined,
      });
      toast.success("Agreement signed — you're enrolled as a Game Manager");
      await load();
      navigate("/manager");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign the agreement");
    } finally { setBusy(false); }
  };

  if (!doc) {
    return (
      <Layout>
        <div className="text-white/40 py-20 text-center font-display text-2xl tracking-widest animate-pulse">
          LOADING...
        </div>
      </Layout>
    );
  }

  const t = doc.terms;

  return (
    <Layout>
      <div className="max-w-3xl">
        <Link to="/manager" data-testid="agreement-back-link"
          className="text-white/40 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1.5 mb-4 w-fit">
          <ArrowLeft size={12} /> Manager hub
        </Link>

        <div className="text-[#FFD700] text-xs uppercase tracking-widest mb-2 inline-flex items-center gap-1.5">
          <FileSignature size={13} /> Independent contractor · v{t.version}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">GAME MANAGER AGREEMENT</h1>
        <p className="text-white/50 text-sm mt-2">
          Between <strong className="text-white/80">{t.company}</strong> and you, the Game Manager.
          Read it, sign it, then start running games for your crew.
        </p>

        <div className="grid sm:grid-cols-3 gap-3 my-6">
          {[
            { label: "Revenue share", value: `${t.share_pct.toFixed(0)}%`, note: "of net subscription revenue you bring in" },
            { label: "Game cap", value: `${t.game_cap}`, note: "games per calendar month" },
            { label: "Payout threshold", value: `$${Number(doc.payout_threshold).toFixed(0)}`, note: "settled monthly by email" },
          ].map((s) => (
            <div key={s.label} className="border border-white/10 bg-[#141414] p-4"
              data-testid={`agreement-term-${s.label.split(" ")[0].toLowerCase()}`}>
              <div className="text-white/40 text-[10px] uppercase tracking-widest">{s.label}</div>
              <div className="font-display text-3xl text-[#FFD700] mt-1">{s.value}</div>
              <div className="text-white/40 text-[11px] mt-1">{s.note}</div>
            </div>
          ))}
        </div>

        {doc.signed && (
          <div className="border border-[#00FF66]/40 bg-[#00FF66]/[0.06] p-4 mb-6"
            data-testid="agreement-signed-banner">
            <div className="text-[#00FF66] text-xs uppercase tracking-widest inline-flex items-center gap-1.5">
              <ShieldCheck size={13} /> Signed
            </div>
            <div className="text-sm mt-1">
              <strong>{doc.record.full_name}</strong> · {new Date(doc.record.signed_at).toLocaleString()}
            </div>
            <div className="text-white/40 text-[11px] mt-0.5">
              Signature "{doc.record.signature}" · version {doc.record.version}
              {doc.record.ip ? ` · IP ${doc.record.ip}` : ""}
            </div>
          </div>
        )}

        <div className="border border-white/10 bg-[#141414] divide-y divide-white/5"
          data-testid="agreement-body">
          {doc.sections.map((s) => (
            <div key={s.heading} className="p-5">
              <div className="font-display text-lg mb-1.5">{s.heading}</div>
              <p className="text-white/60 text-sm leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        <button onClick={() => window.print()} data-testid="agreement-print-btn"
          className="mt-3 text-white/40 hover:text-white text-xs uppercase tracking-widest inline-flex items-center gap-1.5">
          <Printer size={12} /> Print or save a copy
        </button>

        {!doc.signed && (
          <div className="mt-6 border border-[#FFD700]/30 bg-[#FFD700]/[0.05] p-5" data-testid="agreement-sign-form">
            <div className="font-display text-2xl mb-1">SIGN IT</div>
            <p className="text-white/50 text-sm mb-4">
              Type your full legal name in both fields. We store your signature with a timestamp and IP
              address as your electronic signature.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-widest">Full legal name</label>
                <input value={fullName} onChange={(e) => setFullName(e.target.value)}
                  data-testid="agreement-name-input"
                  className="w-full mt-1 bg-[#0a0a0a] border border-white/10 focus:border-[#FFD700] rounded-sm px-3 py-2.5 outline-none" />
              </div>
              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-widest">Typed signature</label>
                <input value={signature} onChange={(e) => setSignature(e.target.value)}
                  placeholder="Same as above"
                  data-testid="agreement-signature-input"
                  className="w-full mt-1 bg-[#0a0a0a] border border-white/10 focus:border-[#FFD700] rounded-sm px-3 py-2.5 outline-none font-display text-xl" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-white/40 text-[10px] uppercase tracking-widest">Payout email (optional)</label>
                <input value={payoutEmail} onChange={(e) => setPayoutEmail(e.target.value)}
                  placeholder="Where should we send payments?"
                  data-testid="agreement-payout-email-input"
                  className="w-full mt-1 bg-[#0a0a0a] border border-white/10 focus:border-[#FFD700] rounded-sm px-3 py-2.5 outline-none" />
              </div>
            </div>
            <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
              <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)}
                data-testid="agreement-accept-checkbox" className="mt-0.5 accent-[#FFD700]" />
              <span className="text-white/60 text-sm">
                I have read the agreement, I am an independent contractor, I will never collect money
                from participants, and I am responsible for my own taxes.
              </span>
            </label>
            <button onClick={sign} disabled={busy || !accept || !fullName.trim() || !signature.trim()}
              data-testid="agreement-sign-btn"
              className="mt-4 w-full py-3 bg-[#FFD700] hover:bg-[#e6c200] disabled:opacity-40 text-black font-bold uppercase tracking-widest text-sm rounded-sm inline-flex items-center justify-center gap-2">
              <Check size={15} /> {busy ? "Signing..." : "Sign and enrol"}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
